import {
  AgeRangeEnumType,
  FederalGroupEnumType,
  HealthInsuranceOrCoverageStatusEnumType,
} from "@libs/api/patients";
import { HouseholdMemberType } from "@/domain/householdMember";

import { convertDateOfBirthToAgeRange } from "./eligibility";

type EligibilityCheckType = {
  householdMember: HouseholdMemberType;
  ageRange: AgeRangeEnumType;
};

/**
 * Determines which federal groups a household member is eligible for based on their demographics and status fields
 */
export const determineEligibleFederalGroups = (
  householdMember: HouseholdMemberType,
): FederalGroupEnumType[] => {
  // Filter out household members without valid ageRange or dateOfBirth
  const ageRange =
    householdMember.ageRange ??
    convertDateOfBirthToAgeRange(householdMember.dateOfBirth);

  if (!ageRange) {
    return [];
  }

  const eligibleGroups: FederalGroupEnumType[] = [];
  const { citizenshipStatus, maritalStatus, healthInsuranceOrCoverageStatus } =
    householdMember;

  const isEligibleCitizenship =
    citizenshipStatus === "US_CITIZEN" ||
    citizenshipStatus === "IMMIGRANT" ||
    citizenshipStatus === null;

  if (!isEligibleCitizenship) {
    return [];
  }

  const isSingleMaritalStatus =
    maritalStatus === "NEVER_MARRIED" ||
    maritalStatus === "SEPARATED" ||
    maritalStatus === "DIVORCED" ||
    maritalStatus === "WIDOWED";

  const isMarriedMaritalStatus =
    maritalStatus === "MARRIED" ||
    maritalStatus === "REGISTERED_DOMESTIC_PARTNER" ||
    maritalStatus === "COMMON_LAW";

  const checkEligibilityByAgeRange = ({ ageRange }: EligibilityCheckType) => {
    // Child eligibility groups
    if (ageRange === "BABY") {
      eligibleGroups.push("CHILD_0_1");
    }

    if (ageRange === "CHILD") {
      eligibleGroups.push("CHILD_1_5");
    }

    if (ageRange === "YOUTH") {
      eligibleGroups.push("CHILD_6_18");
    }

    if (ageRange === "CHILD" || ageRange === "YOUTH") {
      eligibleGroups.push("CHILD_1_18");
    }

    if (ageRange === "BABY" || ageRange === "CHILD" || ageRange === "YOUTH") {
      eligibleGroups.push("CHILD_0_18_CHIP");
    }

    // Young adult eligibility
    if (ageRange === "YOUNG_ADULT") {
      eligibleGroups.push("ADULT_19_20");
    }

    // Expansion adults eligibility (TURNING_SENIOR is age 64, still within 19-64)
    if (
      ageRange === "YOUNG_ADULT" ||
      ageRange === "ADULT" ||
      ageRange === "TURNING_SENIOR"
    ) {
      eligibleGroups.push("EXPANSION_ADULTS_19_64");
    }

    // Senior-related eligibility groups (TURNING_SENIOR gets the same Medicare
    // AGE privileges as SENIOR since they are approaching Medicare eligibility)
    if (ageRange === "SENIOR" || ageRange === "TURNING_SENIOR") {
      if (isSingleMaritalStatus) {
        eligibleGroups.push("AGE_SINGLE");
      }

      if (isMarriedMaritalStatus) {
        eligibleGroups.push("AGE_MARRIED");
      }

      // Medicare AGE eligibility
      const hasHealthInsuranceCoverage = healthInsuranceOrCoverageStatus?.some(
        status =>
          status === "MEDICAID" ||
          status === "TRICARE" ||
          status === "VA" ||
          status === "PRIVATE" ||
          status === "OTHER",
      );

      if (
        hasHealthInsuranceCoverage ||
        !healthInsuranceOrCoverageStatus ||
        healthInsuranceOrCoverageStatus.length === 0
      ) {
        eligibleGroups.push("AGE");
      }
    }
  };

  checkEligibilityByAgeRange({ householdMember, ageRange });

  // Pregnancy eligibility
  if (householdMember.pregnantStatus === "PREGNANT") {
    eligibleGroups.push("PREGNANT_WOMEN");
  }

  // Caregiver eligibility
  if (householdMember.childCaregiverStatus === "CAREGIVER") {
    eligibleGroups.push("PARENT_CARETAKER");
  }

  // Cancer eligibility
  if (householdMember.breastCancerStatus === "BREAST_CANCER") {
    eligibleGroups.push("BREAST_CANCER");
  }

  if (householdMember.cervicalCancerStatus === "CERVICAL_CANCER") {
    eligibleGroups.push("CERVICAL_CANCER");
  }

  // Blind/vision loss eligibility
  if (householdMember.blindOrVisionLossStatus === "BLIND_OR_VISION_LOSS") {
    if (isSingleMaritalStatus) {
      eligibleGroups.push("BLIND_SINGLE");
    }

    if (isMarriedMaritalStatus) {
      eligibleGroups.push("BLIND_MARRIED");
    }
  }

  // Disabled eligibility
  if (householdMember.disabledStatus === "DISABLED") {
    if (isSingleMaritalStatus) {
      eligibleGroups.push("DISABLED_SINGLE");
    }

    if (isMarriedMaritalStatus) {
      eligibleGroups.push("DISABLED_MARRIED");
    }

    // Medicare DISABLED eligibility
    const hasHealthInsuranceCoverage = healthInsuranceOrCoverageStatus?.some(
      (status: HealthInsuranceOrCoverageStatusEnumType) =>
        status === "MEDICAID" ||
        status === "EMPLOYER" ||
        status === "TRICARE" ||
        status === "VA" ||
        status === "PRIVATE" ||
        status === "OTHER",
    );

    if (
      hasHealthInsuranceCoverage ||
      !healthInsuranceOrCoverageStatus ||
      healthInsuranceOrCoverageStatus.length === 0
    ) {
      eligibleGroups.push("DISABLED");
    }
  }

  // Foster care eligibility
  if (householdMember.fosterCareFormerlyStatus === "FORMER_FOSTER") {
    eligibleGroups.push("FORMERLY_FOSTER_CARE");
  }

  // No employer coverage eligibility (Marketplace)
  const hasHealthInsuranceCoverage = healthInsuranceOrCoverageStatus?.some(
    (status: HealthInsuranceOrCoverageStatusEnumType) =>
      status === "TRICARE" || status === "VA" || status === "OTHER",
  );

  if (
    hasHealthInsuranceCoverage ||
    !healthInsuranceOrCoverageStatus ||
    healthInsuranceOrCoverageStatus.length === 0
  ) {
    eligibleGroups.push("NO_EMPLOYER_COVERAGE");
  }

  return eligibleGroups;
};
